import { ArrowForwardIosSharp } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Divider, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getDonorEmailLog } from 'src/shared/services/donorService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { MemberEmail } from 'src/modules/member/models/memberEmail';

type DonorEmailLogsModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  donorId: number;
  donorName: string;
  donorEmail?: string;
};

export const DonorEmailLogsModal = (props: DonorEmailLogsModalProps) => {
  const { modalOpen, onClose, donorId, donorName, donorEmail } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [expandedPanel, setExpandedPanel] = useState<number | false>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [emailLogs, setEmailLogs] = useState<MemberEmail[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getDonorEmailLog(donorId, 50);
      if (isMounted()) {
        setEmailLogs(result.data.results ?? []);
      }
    } catch (err) {
      if (isMounted()) ConsoleHelper.log(err);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, donorId]);

  useEffect(() => {
    if (donorId) {
      fetchData();
    }
  }, [fetchData, donorId]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={() => onClose()}
      onSave={() => onClose()}
      padding={0}
      showDialogActions={false}
      dialogTitle={t('member:emailLog.title')}
      dialogSubTitle={donorName}
      dialogContent={
        <Grid container>
          <Grid item xs={12}>
            <LoadingOverlay loading={loading} />
            {donorEmail && (
              <Alert severity='info' sx={{ mx: 2, mt: 2 }}>
                {t('gift:emailLogInfo', { email: donorEmail })}
              </Alert>
            )}
            <Box
              sx={{
                '& .MuiAccordionSummary-root': {
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                    transform: 'rotate(90deg)',
                  },
                },
              }}
            >
              {emailLogs.map((log) => (
                <Accordion
                  key={log.mail_id}
                  expanded={expandedPanel === log.mail_id}
                  onChange={() => setExpandedPanel(expandedPanel === log.mail_id ? false : log.mail_id)}
                  disableGutters
                  elevation={0}
                  sx={{ '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />} sx={{ px: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', ml: 1 }}>
                      <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        {log.subject}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap', ml: 2 }}>
                        {DateHelper.getDateTime(log.circularExecutingTimeStamp)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    {/* eslint-disable-next-line react/no-danger */}
                    <div dangerouslySetInnerHTML={{ __html: log.body || '' }} />
                  </AccordionDetails>
                </Accordion>
              ))}
              {!loading && emailLogs.length === 0 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant='body2' color='text.secondary'>
                    {t('member:emailLog.noEmails')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      }
    />
  );
};
